import dayjs, { Dayjs } from 'dayjs';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { RegisterBanksSelect } from '@/modules/common/components/RegisterBanksSelect.tsx';
import {
  IssuerAddUpdatePortfolioTransactionModal,
  IssuerAddUpdatePortfolioTransactionModalProps,
  listTypeWithBuy,
  listTypeWithSell,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdatePortfolioTransactionModal.tsx';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { IssuerPortfolioTransactionFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioTransactionFullInfoModal.tsx';
import { SelectAsset } from '@/modules/issuer-dashboard/components/portfolios/SelectAsset.tsx';
import {
  useIssuerDeletePortfolioTransactionMutation,
  useIssuerPortfolioTransactionsInfiniteListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  ModalProviderContext,
  SelectPaginateOption,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon } from 'ui/icons';
import { goodFormatNumberCustom } from 'ui/utils';
import {
  AssetExtended,
  GetPortfolioTransactionListV1SelfCustodyPortfolioTransactionGetParams,
  PortfolioTransactionExtended,
  PortfolioTransactionStatus,
  PortfolioTransactionType,
  RegisterBankExtended,
} from 'api';

type PortfolioTransactionsTable =
  | 'ID'
  | 'Type'
  | 'Asset / Broker'
  | 'Balance account'
  | 'Balance'
  | 'Quantity'
  | 'Price'
  | 'Fee'
  | 'Date'
  | '';

const tableHeadItems: TableHeadColumn<PortfolioTransactionsTable>[] = [
  { title: 'ID' },
  { title: 'Type', className: 'min-w-[200px]' },
  { title: 'Asset / Broker', className: 'min-w-[200px]' },
  { title: 'Balance account' },
  { title: 'Balance' },
  { title: 'Quantity' },
  { title: 'Price' },
  { title: 'Fee' },
  { title: 'Date' },
  { title: '' },
];

export interface PortfolioTransactionsTabProps {
  portfolioId: number;
}

export const PortfolioTransactionsTab = ({ portfolioId }: PortfolioTransactionsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();
  const [filters, setFilters] =
    useState<GetPortfolioTransactionListV1SelfCustodyPortfolioTransactionGetParams>({
      portfolio_id: portfolioId,
    });
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    isFetching,
    data: dataList,
  } = useIssuerPortfolioTransactionsInfiniteListQuery({
    page: 1,
    page_size: 30,
    ...filters,
  });

  const handleOpenFullInfoModal = useCallback(
    (item: PortfolioTransactionExtended) => {
      openModal(IssuerPortfolioTransactionFullInfoModal, {
        id: item.id,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const handleOpenFullAssetInfoModal = useCallback(
    (asset?: AssetExtended) => {
      if (!asset) return;
      openModal(IssuerPortfolioAssetFullInfoModal, {
        name: asset?.name,
        id: asset?.id,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataList?.pages?.forEach(page => {
      result = result.concat(
        page.data?.map(item => {
          const {
            id,
            transaction_type,
            asset,
            broker,
            quantity,
            amount,
            confirmation_date,
            from_bank,
            to_bank,
            price_per_share,
            status,
            fee = 0,
            is_conversion,
            to_currency,
            ai_status,
          } = item;
          const currency = (
            asset?.currency ||
            from_bank?.bank_currency ||
            to_bank?.bank_currency ||
            to_currency
          )?.toUpperCase();

          const isDividendReinvest =
            transaction_type === PortfolioTransactionType.IncomeDividendReinvestment;
          const isFee = transaction_type === PortfolioTransactionType.Fee;
          const isRevaluation = transaction_type === PortfolioTransactionType.Revaluation;
          const isSplit = transaction_type === PortfolioTransactionType.Split;
          const isBrokerWithdraw =
            transaction_type === PortfolioTransactionType.BrokerWithdraw && !is_conversion;
          const isBuy = listTypeWithBuy.includes(transaction_type);
          const isSell = listTypeWithSell.includes(transaction_type);
          const isBrokerSell = isSell && !!broker;
          const showsAssetQuantity = isBuy || isSell || isDividendReinvest || isSplit;

          const body: BodyCell[] = [
            {
              cell: <div className="text-blue-700">{id}</div>,
            },
            {
              cell: (
                <PortfolioTransactionTypeBlock
                  type={transaction_type}
                  isSettle={status === PortfolioTransactionStatus.Completed}
                  isConversion={is_conversion}
                  additionalInfo={isSplit ? (+quantity < 0 ? 'Reverse' : 'Forward') : undefined}
                  aiStatus={ai_status}
                />
              ),
            },
            {
              cell: asset?.name ? (
                <span className="flex gap-2 items-center">
                  Asset:
                  <div
                    className="text-blue-700 cursor-pointer hover:underline"
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenFullAssetInfoModal(asset);
                    }}
                  >
                    {asset?.name}
                  </div>
                </span>
              ) : broker?.name && !is_conversion ? (
                <span className="flex gap-2 items-center">
                  Broker:
                  <div className="text-blue-700">{broker?.name}</div>
                </span>
              ) : (
                '-'
              ),
            },
            {
              cell: from_bank ? (
                <span className="flex gap-2 items-center">
                  Bank:
                  <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                    <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                      <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                        {from_bank?.bank_account_name}
                      </span>
                    </span>
                    <span className="text-gray-400">{from_bank?.bank_currency.toUpperCase()}</span>
                  </span>
                </span>
              ) : to_bank ? (
                <span className="flex gap-2 items-center">
                  Bank:
                  <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                    <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                      <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                        {to_bank?.bank_account_name}
                      </span>
                    </span>
                    <span className="text-gray-400">{to_bank?.bank_currency.toUpperCase()}</span>
                  </span>
                </span>
              ) : (isBuy || isSell || is_conversion) && broker ? (
                <span className="flex gap-2 items-center">
                  Broker:
                  <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                    <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                      <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                        {broker?.name}
                      </span>
                    </span>
                    <span className="text-gray-400">{currency?.toUpperCase() || ''}</span>
                  </span>
                </span>
              ) : (
                '-'
              ),
            },
            {
              cell: (
                <div className="whitespace-nowrap text-nowrap">
                  {amount && +amount ? (
                    <>
                      {isFee || isBrokerWithdraw ? '-' : '+'}
                      {goodFormatNumberCustom(amount, currency, 0, 2)}
                    </>
                  ) : isRevaluation || isSplit || isDividendReinvest || +price_per_share === 0 ? (
                    <>-</>
                  ) : (
                    <>
                      {to_bank || isBrokerSell ? '+' : '-'}
                      {goodFormatNumberCustom(+price_per_share * +quantity, currency, 0, 2)}
                    </>
                  )}
                </div>
              ),
            },
            {
              cell: (
                <div className="whitespace-nowrap text-nowrap">
                  {isRevaluation ? (
                    <>0</>
                  ) : !showsAssetQuantity ? (
                    <>-</>
                  ) : (
                    <>
                      {from_bank || isBuy || isDividendReinvest || (isSplit && +quantity >= 0)
                        ? '+'
                        : isSplit
                        ? ''
                        : '-'}
                      {quantity ? +quantity : 0}
                    </>
                  )}
                </div>
              ),
            },
            {
              cell: (
                <>
                  {price_per_share && +price_per_share > 0
                    ? goodFormatNumberCustom(+price_per_share, currency, 2, 6)
                    : '-'}
                </>
              ),
            },
            {
              cell: (
                <>
                  {fee && +fee > 0 ? (
                    <div>
                      {'-'}
                      {goodFormatNumberCustom(+fee, currency, 0, 2)}
                    </div>
                  ) : (
                    '-'
                  )}
                </>
              ),
            },
            {
              cell: (
                <div>
                  {confirmation_date ? dayjs(confirmation_date).format('DD/MM/YYYY HH:mm') : '-'}
                </div>
              ),
            },
            { cell: <MoreButtonTransaction item={item} portfolioId={portfolioId} /> },
          ];

          return {
            cells: body,
            onClick: () => handleOpenFullInfoModal(item),
          };
        }),
      );
    });
    return result;
  }, [dataList, handleOpenFullInfoModal, portfolioId]);

  const handleOpenAddTransactionModal = useCallback(() => {
    openModal(IssuerAddUpdatePortfolioTransactionModal, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  const dateValue =
    filters.date_from && filters.date_to
      ? ([dayjs(filters.date_from), dayjs(filters.date_to)] as [Dayjs, Dayjs])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              date_from: date[0].toISOString(),
              date_to: date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']);
      });
    },
    [setFilters],
  );

  const handleChangeAsset = useCallback(
    (option: SelectPaginateOption<AssetExtended> | null) => {
      setFilters(filters =>
        option ? { ...filters, asset_id: option?.value?.id } : omit(filters, 'asset_id'),
      );
    },
    [setFilters],
  );

  const handleChangeBank = useCallback(
    (option: SelectPaginateOption<RegisterBankExtended> | null) => {
      setFilters(filters =>
        option ? { ...filters, bank_id: option?.value?.id } : omit(filters, 'bank_id'),
      );
    },
    [setFilters],
  );

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full">
          <FilterItem type="custom" className="rounded-lg" classes={{ element: 'py-0' }}>
            <SelectAsset
              portfolioId={portfolioId}
              onChange={handleChangeAsset}
              value={filters.asset_id}
              placeholder="Asset"
              clearable={true}
              showBorderBottom={false}
              className="w-[370px]"
              withBrokers={false}
            />
          </FilterItem>
          <FilterItem type="custom" className="rounded-lg" classes={{ element: 'py-0' }}>
            <RegisterBanksSelect
              onChange={handleChangeBank}
              value={filters.bank_id}
              placeholder="Bank"
              clearable={true}
              showBorderBottom={false}
              className="w-[370px]"
              prefillBank={false}
              hideType={true}
              portfolio_id={portfolioId}
              is_overall={true}
            />
          </FilterItem>
          <FilterItem
            placeholder="Date"
            type="date"
            onChange={handleChangeDate}
            value={dateValue}
            className="rounded-lg"
          />
          {isFetching && <Spinner size="md" variant="primary" />}
        </div>
        <div className="min-w-[142px]">
          <Button className="w-auto" onClick={handleOpenAddTransactionModal}>
            New transaction
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface MoreButtonTransactionProps {
  item: PortfolioTransactionExtended;
  portfolioId: number;
}

export const MoreButtonTransaction = ({ item, portfolioId }: MoreButtonTransactionProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioTransactionMutation();

  const items = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal<IssuerAddUpdatePortfolioTransactionModalProps>(
            IssuerAddUpdatePortfolioTransactionModal,
            {
              item,
              portfolioId: portfolioId,
            },
          );
        },
      },
      {
        label: 'Archive',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Archive</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal, portfolioId]);

  const isNotSettled =
    item.status === PortfolioTransactionStatus.Pending && item.transaction_type?.includes('book');

  const handleOpenAddTransactionModal = useCallback(() => {
    openModal(IssuerAddUpdatePortfolioTransactionModal, {
      portfolioId,
      item,
      isSettle: true,
    });
  }, [item, openModal, portfolioId]);

  return (
    <div className="flex items-center justify-end gap-4">
      {isNotSettled && (
        <div className="w-[75px]">
          <Button variant="secondary" onClick={handleOpenAddTransactionModal}>
            Settle
          </Button>
        </div>
      )}
      <DropdownMenu
        items={items}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        autoPlace
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};

export const mapPortfolioTransactionType: Partial<Record<PortfolioTransactionType, string>> = {
  [PortfolioTransactionType.TradeBuyBook]: 'Buy Book',
  [PortfolioTransactionType.TradeBuySettle]: 'Buy Settle',
  [PortfolioTransactionType.TradeSellBook]: 'Sell Book',
  [PortfolioTransactionType.TradeSellSettle]: 'Sell Settle',
  [PortfolioTransactionType.IncomeDividendReinvestment]: 'Dividend / Distribution Reinvestment',
  [PortfolioTransactionType.IncomeDividendCash]: 'Dividend / Distribution Cash',
  [PortfolioTransactionType.IncomeRental]: 'Rental Income',
  [PortfolioTransactionType.IncomeCashInterest]: 'Cash Interest',
  [PortfolioTransactionType.Revaluation]: 'Revaluation',
  [PortfolioTransactionType.Fee]: 'Fee',
  [PortfolioTransactionType.Split]: 'Split',
  [PortfolioTransactionType.BrokerDeposit]: 'Broker Deposit',
  [PortfolioTransactionType.BrokerWithdraw]: 'Broker Withdraw',
  [PortfolioTransactionType.BrokerCurrencyConversion]: 'Broker Currency Conversion',
};

export interface PortfolioTransactionTypeBlockProps {
  type: PortfolioTransactionType;
  isSettle?: boolean;
  isConversion?: boolean;
  additionalInfo?: string;
  aiStatus?: string | null;
}

export const PortfolioTransactionTypeBlock = ({
  type,
  isSettle = false,
  isConversion = false,
  additionalInfo,
  aiStatus,
}: PortfolioTransactionTypeBlockProps) => {
  return (
    <div className="flex items-center gap-3">
      <span>
        {additionalInfo}{' '}
        {
          mapPortfolioTransactionType[
            isConversion ? PortfolioTransactionType.BrokerCurrencyConversion : type
          ]
        }
      </span>
      {type?.includes('book') &&
        (isSettle ? (
          <span className="bg-[#16A34A1F] rounded-sm text-[#16A34A] px-1.5 text-xs leading-5 font-[Inter]">
            Settled
          </span>
        ) : (
          <></>
          // <span className="bg-[#DC26261F] rounded-sm text-[#DC2626] px-1.5 text-xs leading-5 font-[Inter]">
          //   Not settled
          // </span>
        ))}
      <CreatedByAiLabel aiStatus={aiStatus} />
    </div>
  );
};
